import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";
import { copyTextToClipboard, defaultCellRenderer, getOriginalRecord, handleSocketChangesOnGridStore, showConfirmationDialog } from "@/other/utils/utils";
import { cellMenuItemCopyContactToClipboard, cellMenuItemRemoveRow } from "@/other/utils/gridCellMenuUtils";
import { CONTACT_VIEW_COLUMNS } from "@/other/constants/ContactViewColumns";
import { contactGridRowExpander } from "@/other/constants/ContactViewGridConfig";
import { callOnLoadOnColumns, canOpenEditor, debounce } from "@/other/utils/otherUtils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { MessageDialog, Store } from "@bryntum/schedulerpro";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { useMemo, useRef, useEffect, useCallback } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { User } from "@/bryntum-lib/models/User";
import { Button } from "@/modern/components/ui/button";
import { Plus } from "lucide-react";

const entityContactColumns = CONTACT_VIEW_COLUMNS.map((contactColumn) => {
  return { ...contactColumn, field: `contactRecord.${contactColumn.field}` };
});

const entityContactRowExpander = { ...contactGridRowExpander, dataField: `contactRecord.${contactGridRowExpander.dataField}` };

export const ContactGrid = ({ userRecord }: { userRecord: User }) => {
  const gridRef = useRef<any>(null);
  const debouncedCellClickRef = useRef<any>(null);
  const { project, user, editorHandler, entityManager } = useSwivuBryntumContext();

  // Handle adding a new contact
  const onAddContact = useCallback(() => {
    const newRecord = project.contactsStore.createRecord({});
    editorHandler.openEditor(
      EDITOR_KEYS.contactEditor,
      { id: newRecord.id },
      {
        contact: newRecord,
        entityRecord: userRecord
      },
      {
        by: "recordType",
        useExisting: true
      }
    );
  }, [project, editorHandler, userRecord]);

  // Handle deleting contacts from list
  const onDeleteFromList = useCallback(() => {
    const grid = gridRef.current?.instance;
    if (!grid) return;

    const contactsToRemoveFromList = grid.selectedRecords;
    if (contactsToRemoveFromList.length) {
      // prevents user self contact record to be removed from the contact list
      if (userRecord.selfContactId === contactsToRemoveFromList[0].contactRecord.id) {
        MessageDialog.alert({
          title: "Cannot Remove User's Self Contact",
          message: "This is the user's self contact record and it cannot be removed from the contact list."
        });
      } else {
        const contactNames = contactsToRemoveFromList.map((entityContact: any) => defaultCellRenderer({ value: entityContact.contactRecord.name })).join("<br/>");

        showConfirmationDialog({
          title: "Delete Contact(s) From List",
          message: `<div>Are you sure you want to delete the following contact(s) from the list?<br/><br/>${contactNames}</div>`,
          confirmBtnText: "Delete",
          cancelBtnText: "Cancel",
          isCloseByHeader: false
        }).then((isDeleteFromList) => {
          if (isDeleteFromList) {
            project.userContactsStore.remove(contactsToRemoveFromList);
            grid.store.remove(contactsToRemoveFromList);
          }
        });
      }
    }
  }, [project, userRecord, gridRef]);

  // Handle cell clicks
  const onClickEntity = useCallback(
    ({ event, column, record }: any) => {
      const { id, type } = event.target.dataset;

      if (column.id === id && type === "iconType") {
        copyTextToClipboard(record.contactRecord[id], `Copied ${id} to clipboard`);
        return;
      }

      if (column.id === "entities") {
        const { id, type } = event.target.dataset;
        if (type === "Task") {
          const task = project.extraTaskStore.getById(id);
          if (task) {
            entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: task.id });
          }
        } else if (type === "Resource") {
          const resource = project.resourceStore.getById(id);
          if (resource) {
            entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: resource.id });
          }
        } else if (type === "Client") {
          const client = project.clientsStore.getById(id);
          if (client) {
            entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: client.id });
          }
        } else if (type === "User") {
          const user = project.boardUsersStore.getById(id);
          if (user) {
            editorHandler.openEditor(EDITOR_KEYS.userEditor, user as any, { user: user });
          }
        }
      } else {
        const contactRecord = record.contactRecord;
        if (debouncedCellClickRef.current) {
          debouncedCellClickRef.current({ column, record: contactRecord, event });
        }
      }
    },
    [project, editorHandler, entityManager]
  );

  // Handle copying contacts
  const onCopyContact = useCallback((e: any) => {
    const selectedContacts = e.selection.map((entityContact: any) => entityContact.contactRecord);
    if (selectedContacts.length) {
      const contactsConcat = selectedContacts.map((contact: any) => {
        const contactName = defaultCellRenderer({ value: contact.name });
        const contactPhone = defaultCellRenderer({ value: contact.phone });
        const contactEmail = defaultCellRenderer({ value: contact.email });
        const contactAddress = defaultCellRenderer({ value: contact.address });
        return `${contactName}, ${contactPhone}, ${contactEmail}, ${contactAddress}`;
      });
      copyTextToClipboard(contactsConcat.join("\n"), `Selected ${selectedContacts.length > 1 ? selectedContacts.length + " contacts" : "contact"} copied to clipboard.`);
    }
  }, []);

  // Create debounced cell click handler
  useEffect(() => {
    debouncedCellClickRef.current = debounce(({ column, record, event }: any) => {
      const originalRecord = getOriginalRecord(record);
      if (
        (canOpenEditor(column, originalRecord) && event.detail === 1 && column.type === WIDGET_KEYS.columnContactStatus) ||
        column.type === WIDGET_KEYS.columnContactCategory ||
        column.type === WIDGET_KEYS.columnContactTag
      ) {
        editorHandler.openEditor(
          EDITOR_KEYS.entityTagEditor,
          null as any,
          {
            entityRecord: originalRecord,
            event,
            column
          },
          { by: "recordType", useExisting: true }
        );
      }
    }, 200);

    return () => {
      if (debouncedCellClickRef.current) {
        debouncedCellClickRef.current = null;
      }
    };
  }, [editorHandler]);

  // Setup grid and load data
  useEffect(() => {
    const grid = gridRef.current?.instance;
    if (!grid || !userRecord) return;

    // Load contacts data
    const entityContacts = userRecord.contacts;
    if (entityContacts) {
      grid.store = new Store({
        data: entityContacts.sort((a: any, b: any) => a.order - b.order)
      });
    }

    // Setup columns
    callOnLoadOnColumns(grid.columns, project, {
      editorHandler,
      editorNameFieldPrefix: `contactRecord.`
    });

    // Handle socket changes
    handleSocketChangesOnGridStore(grid, project.userContactsStore, () => userRecord.contacts);

    // Check grants
    if (user && !user.can(GRANTS.grant_four)) {
      grid.readOnly = true;
    }
  }, [userRecord, project, editorHandler, user, onAddContact, onDeleteFromList, onClickEntity, onCopyContact]);

  const gridConfig = useMemo(() => {
    return {
      height: "100%",
      columns: entityContactColumns,
      features: {
        rowExpander: entityContactRowExpander,
        rowReorder: { showGrip: true, disabled: true },
        cellMenu: {
          items: {
            cut: false,
            copy: false,
            paste: false,
            copyToClipboard: cellMenuItemCopyContactToClipboard({
              onItem: onCopyContact
            }),
            removeRow: cellMenuItemRemoveRow({
              onItem: onDeleteFromList,
              icon: "fa-regular fa-trash !text-danger"
            })
          }
        }
      },
      listeners: {
        cellClick: onClickEntity
      }
    };
  }, [onCopyContact, onClickEntity, onDeleteFromList]);

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex justify-between items-center px-2">
        <h5 className="text-lg font-medium">Contacts</h5>
        <Button onClick={onAddContact}>
          <Plus className="size-4" />
          Add Contact
        </Button>
      </div>

      {/* @ts-expect-error bryntum widget throws error on passing config as a prop */}
      <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuGrid} config={gridConfig} />
    </div>
  );
};
